import { configService } from '../../services/configService';
import authService from '../../services/authService';
import { wasabiBackupService } from '../../services/wasabiBackupService';
import { grafanaMetricsService } from '../../services/grafanaMetricsService';
import { serviceConnectionsService } from '../../services/serviceConnectionsService';
import { supabase } from '../../services/supabaseService';
import { structuredLogger } from '../../services/structuredLogger';
import { useVersionTracking } from '../../hooks/useVersionTracking';
import { mapStateToSystemConfig, mapStorageConnectionToState } from './settingsHelpers';
import type { SettingsState } from './useSettingsState';
import type { UseSettingsLoaders } from './useSettingsLoaders';

export function useSettingsActions(
  state: SettingsState,
  loaders: UseSettingsLoaders
) {
  const { trackSettingsAction } = useVersionTracking();
  const { configs, setters, ui } = state;

  const togglePasswordVisibility = (field: string) => {
    ui.setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const testConnection = async (service: string) => {
    ui.setTestResults(prev => ({ ...prev, [service]: null }));

    try {
      let result = false;

      switch (service) {
        case 'firebase':
          result = await configService.testFirebaseConnectionBasic();
          break;
        case 'google':
          result = await configService.testGoogleConnection();
          break;
        case 'sendgrid':
          result = await configService.testSendGridConnection();
          break;
        case 'openai':
          result = await configService.testOpenAIConnection();
          if (result) {
            await loaders.loadOpenAIBillingInfo();
          }
          break;
        case 'resend': {
          const { resendService } = await import('../../services/resendService');
          resendService.saveConfig({
            apiKey: configs.resendConfig.apiKey,
            fromEmail: configs.resendConfig.fromEmail,
            fromName: configs.resendConfig.fromName,
            isConfigured: false
          });
          result = await resendService.testConnection();
          break;
        }
      }

      ui.setTestResults(prev => ({ ...prev, [service]: result }));

      if (result) {
        alert(`✅ Conexión con ${service} exitosa`);
      } else {
        alert(`❌ Error conectando con ${service}. Verifica las credenciales.`);
      }
    } catch (error: any) {
      console.error(`Error testing ${service}:`, error);
      ui.setTestResults(prev => ({ ...prev, [service]: false }));
      alert(`❌ Error probando ${service}: ${error.message}`);
    }
  };

  const handleTestGrafanaConnection = async () => {
    ui.setTestingGrafanaConnection(true);
    ui.setGrafanaTestResult(null);
    try {
      grafanaMetricsService.updateConfig({
        enabled: configs.grafanaConfig.enabled,
        url: configs.grafanaConfig.url,
        apiKey: configs.grafanaConfig.apiKey,
        lokiUrl: configs.grafanaConfig.lokiUrl,
        dashboardId: configs.grafanaConfig.dashboardId
      });

      const result = await grafanaMetricsService.testConnection();
      ui.setGrafanaTestResult(result.success ? 'success' : result.message);
    } catch (_error) {
      ui.setGrafanaTestResult('Error al probar conexión');
    } finally {
      ui.setTestingGrafanaConnection(false);
    }
  };

  const handleTestStorageConnection = async () => {
    try {
      const result = await wasabiBackupService.testWasabiConnection({
        endpoint: configs.storageConfig.endpoint,
        access_key: configs.storageConfig.accessKey,
        secret_key: configs.storageConfig.secretKey,
        bucket_name: configs.storageConfig.bucketName,
        region: configs.storageConfig.region
      });
      ui.setTestResults(prev => ({ ...prev, storage: result }));
      if (result) {
        alert('✅ Conexión exitosa con Wasabi');
      } else {
        alert('❌ Error de conexión. Verifica las credenciales.');
      }
    } catch (error: any) {
      ui.setTestResults(prev => ({ ...prev, storage: false }));
      alert('❌ Error al probar la conexión: ' + error.message);
    }
  };

  const handleReloadStorageFromDB = async () => {
    try {
      console.log('🔄 Recargando configuración desde base de datos...');
      const storageConnection = await wasabiBackupService.getStorageConnection(null);

      if (storageConnection && storageConnection.service_type === 'wasabi') {
        setters.setStorageConfig(mapStorageConnectionToState(storageConnection));
        alert('✅ Configuración recargada desde base de datos');
      } else {
        alert('ℹ️ No hay configuración guardada en base de datos');
      }
    } catch (error: any) {
      console.error('Error recargando configuración:', error);
      alert('❌ Error al recargar la configuración: ' + error.message);
    }
  };

  const testBudgetConnection = async () => {
    if (!configs.budgetConfig.apiUrl || !configs.budgetConfig.apiKey) {
      ui.setBudgetTestResult({
        success: false,
        message: 'Por favor completa todos los campos antes de probar'
      });
      return;
    }

    ui.setTestingBudgetConnection(true);
    ui.setBudgetTestResult(null);

    try {
      const response = await fetch(
        `${configs.budgetConfig.apiUrl}/functions/v1/get-pending-proposals`,
        {
          headers: {
            'Authorization': `Bearer ${configs.budgetConfig.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        ui.setBudgetTestResult({
          success: false,
          message: `Error de conexión: HTTP ${response.status} - ${errorText}`
        });
        return;
      }

      const result = await response.json();

      ui.setBudgetTestResult({
        success: true,
        message: `Conexión exitosa. Se encontraron ${result.count || 0} propuestas.`
      });

      const user = (await supabase.auth.getUser()).data.user;

      await supabase
        .from('budget_sync_config')
        .upsert({
          service_name: 'bolt_database',
          api_url: configs.budgetConfig.apiUrl,
          api_key: configs.budgetConfig.apiKey,
          is_active: true,
          last_tested_at: new Date().toISOString(),
          test_status: 'success',
          test_message: `Conexión exitosa. ${result.count || 0} propuestas encontradas.`,
          updated_by: user?.id
        });

      await loaders.loadBudgetConfig();
    } catch (error: any) {
      ui.setBudgetTestResult({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      ui.setTestingBudgetConnection(false);
    }
  };

  const saveBudgetConfig = async () => {
    if (!configs.budgetConfig.apiUrl || !configs.budgetConfig.apiKey) {
      alert('Por favor completa todos los campos');
      return;
    }

    ui.setSavingBudgetConfig(true);

    try {
      const user = (await supabase.auth.getUser()).data.user;

      const { error } = await supabase
        .from('budget_sync_config')
        .upsert({
          service_name: 'bolt_database',
          api_url: configs.budgetConfig.apiUrl,
          api_key: configs.budgetConfig.apiKey,
          is_active: true,
          updated_by: user?.id
        });

      if (error) throw error;

      alert('Configuración guardada exitosamente');
      await loaders.loadBudgetConfig();
    } catch (error: any) {
      structuredLogger.error('Error saving budget sync config', { error });
      alert(`Error al guardar: ${error.message}`);
    } finally {
      ui.setSavingBudgetConfig(false);
    }
  };

  const saveConfiguration = async () => {
    ui.setIsSaving(true);
    try {
      console.log('💾 Guardando configuración en base de datos...');

      const configToSave = mapStateToSystemConfig({
        generalConfig: configs.generalConfig,
        firebaseConfig: configs.firebaseConfig,
        googleConfig: configs.googleConfig,
        emailConfig: configs.emailConfig,
        openaiConfig: configs.openaiConfig,
        resendConfig: configs.resendConfig,
        grafanaConfig: configs.grafanaConfig
      });

      await configService.saveConfigAsync(configToSave);

      if (
        configs.storageConfig.serviceType === 'wasabi' &&
        configs.storageConfig.endpoint &&
        configs.storageConfig.accessKey &&
        configs.storageConfig.secretKey &&
        configs.storageConfig.bucketName
      ) {
        try {
          console.log('💾 Guardando configuración de Wasabi en base de datos...');

          const session = authService.getCurrentSession();
          if (!session?.user?.id) {
            console.error('❌ No hay usuario autenticado');
            alert('Error: Debes estar autenticado para guardar la configuración');
            ui.setIsSaving(false);
            return;
          }

          await wasabiBackupService.saveStorageConnection(
            'wasabi',
            {
              endpoint: configs.storageConfig.endpoint,
              access_key: configs.storageConfig.accessKey,
              secret_key: configs.storageConfig.secretKey,
              bucket_name: configs.storageConfig.bucketName,
              region: configs.storageConfig.region
            },
            null,
            session.user.id
          );

          console.log('✅ Configuración de Wasabi guardada en base de datos');
          setters.setStorageConfig(prev => ({ ...prev, isConfigured: true }));
        } catch (error: any) {
          console.error('❌ Error guardando configuración de Wasabi:', error);
          alert('La configuración general se guardó, pero hubo un error al guardar Wasabi: ' + error.message);
        }
      }

      if (configs.grafanaConfig.enabled && configs.grafanaConfig.url && configs.grafanaConfig.apiKey) {
        try {
          console.log('📊 Guardando configuración de Grafana en base de datos...');

          const session = authService.getCurrentSession();
          if (!session?.user?.id) {
            console.error('❌ No hay usuario autenticado');
            alert('Error: Debes estar autenticado para guardar la configuración');
            ui.setIsSaving(false);
            return;
          }

          await serviceConnectionsService.saveServiceConnection(
            'grafana',
            undefined,
            {
              url: configs.grafanaConfig.url,
              api_key: configs.grafanaConfig.apiKey,
              loki_url: configs.grafanaConfig.lokiUrl,
              dashboard_id: configs.grafanaConfig.dashboardId
            },
            true
          );

          grafanaMetricsService.updateConfig({
            enabled: configs.grafanaConfig.enabled,
            url: configs.grafanaConfig.url,
            apiKey: configs.grafanaConfig.apiKey,
            lokiUrl: configs.grafanaConfig.lokiUrl,
            dashboardId: configs.grafanaConfig.dashboardId
          });

          setters.setGrafanaConfig(prev => ({ ...prev, isConfigured: true }));
          console.log('✅ Configuración de Grafana guardada en base de datos');
        } catch (error: any) {
          console.error('❌ Error guardando configuración de Grafana:', error);
          alert('La configuración general se guardó, pero hubo un error al guardar Grafana: ' + error.message);
        }
      }

      trackSettingsAction(ui.activeTab);

      if (configs.openaiConfig.apiKey) {
        console.log('🔄 Recargando configuración de OpenAI después de guardar...');
        try {
          const { openaiService } = await import('../../services/openaiService');
          await openaiService.reloadConfig();
          console.log('✅ Configuración de OpenAI recargada correctamente');
        } catch (reloadError) {
          console.error('⚠️ Error recargando OpenAI config:', reloadError);
        }
      }

      alert('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Error al guardar la configuración');
    } finally {
      ui.setIsSaving(false);
    }
  };

  return {
    togglePasswordVisibility,
    testConnection,
    handleTestGrafanaConnection,
    handleTestStorageConnection,
    handleReloadStorageFromDB,
    testBudgetConnection,
    saveBudgetConfig,
    saveConfiguration
  };
}

